import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Button,
  Flex,
  HStack,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  Center,
  Spinner,
  Text,
} from "@chakra-ui/react";
import { FaPlus } from "react-icons/fa";
import { SearchIcon } from "@chakra-ui/icons";
import { useNavigate } from "react-router-dom";
import JobList from "./JobList";
import JobViewComponent from "./JobViewComponent";
import { Header } from "build-ts/metadata";
import { listScheduledJobs } from "services/ScheduleServices";

interface AnalystJobsComponentProps {
  analystId: string | undefined;
  setIsViewingOrEditingJob?: (value: boolean) => void;
  analystJobsMap: Map<string, Header[]>;
  setAnalystJobsMap: React.Dispatch<React.SetStateAction<Map<string, Header[]>>>;
  initialJobId?: string;
  /** Analyst display name — used in the breadcrumb. */
  analystName?: string;
}

const AnalystJobsComponent: React.FC<AnalystJobsComponentProps> = ({
  analystId,
  setIsViewingOrEditingJob,
  analystJobsMap,
  setAnalystJobsMap,
  initialJobId,
  analystName,
}) => {
  const navigate = useNavigate();
  const [isCreatingOrViewingJob, setIsCreatingOrViewingJob] = useState<boolean>(false);
  // Start in loading state immediately when deep-linking to a specific job
  const [isLoading, setIsLoading] = useState<boolean>(!!initialJobId);
  const [jobs, setJobs] = useState<Header[]>([]);
  const [selectedJob, setSelectedJob] = useState<Header | undefined>(undefined);
  const [searchTerm, setSearchTerm] = useState<string>("");

  // Filter jobs based on search term
  const filteredJobs = useMemo(() => {
    if (!searchTerm.trim()) {
      return jobs;
    }

    const lowerSearchTerm = searchTerm.toLowerCase();
    return jobs.filter((job) => {
      return job.name?.toLowerCase().includes(lowerSearchTerm);
    });
  }, [jobs, searchTerm]);

  const getJobs = async (analystId: string) => {
    setIsLoading(true);
    if (analystId) {
      const response = await listScheduledJobs(
        "SCENARIO_JOB",
        -1,
        1,
        analystId,
        "LAST_MODIFIED",
        false,
        true, /* includeScheduleActivity */
        "", /* activeSchedule */
      );
      if (response) {
        setJobs(response);
        // Update the map with the new jobs list
        setAnalystJobsMap(prevMap => {
          const newMap = new Map(prevMap);
          newMap.set(analystId, response);
          return newMap;
        });
      }
    } else {
      setJobs([]);
    }
    setIsLoading(false);
  };

  // Initialize jobs from the map, or fetch directly if the map doesn't have them yet
  useEffect(() => {
    if (!analystId) return;
    if (analystJobsMap.has(analystId)) {
      const jobsFromMap = analystJobsMap.get(analystId);
      setJobs(jobsFromMap || []);
    } else {
      // Map not populated yet (e.g. arrived via deep-link before background fetch finished)
      getJobs(analystId);
    }
  }, [analystId, analystJobsMap]);

  // Auto-open a specific job when arriving via deep-link (/jobs/:jobId)
  useEffect(() => {
    if (!initialJobId || jobs.length === 0 || isCreatingOrViewingJob) return;
    const job = jobs.find(j => j.id === initialJobId);
    if (job) {
      handleJobClick(job);
    }
  }, [jobs, initialJobId]);

  const handleCreateNewJob = () => {
    setSelectedJob(undefined);
    setIsCreatingOrViewingJob(true);
    setIsViewingOrEditingJob?.(true);
    if (analystId) {
      navigate(`/analyst/${analystId}/jobs`, { replace: true });
    }
  };

  const handleJobClick = (job: Header) => {
    setSelectedJob(job);
    setIsCreatingOrViewingJob(true);
    setIsViewingOrEditingJob?.(true);
    if (analystId && job.id) {
      navigate(`/analyst/${analystId}/jobs/${job.id}`, { replace: true });
    }
  };

  const handleBackToList = () => {
    setIsCreatingOrViewingJob(false);
    setSelectedJob(undefined);
    setIsViewingOrEditingJob?.(false);
    if (analystId) {
      navigate(`/analyst/${analystId}/jobs`, { replace: true });
      getJobs(analystId);
    }
  };

  const handleRefresh = () => {
    if (analystId) {
      getJobs(analystId);
    }
  };

  if (isCreatingOrViewingJob) {
    const pathBase = `/analyst/${analystId}`;
    const isCreatingNew = !selectedJob;
    const jobDisplayName = selectedJob?.name?.trim() || "New Workflow";

    return (
      <Flex direction="column" height="100%" bg="white" flex="1" minH={0}>
        {/* Breadcrumb */}
        <HStack
          px={3}
          pt={2}
          pb={0}
          flexWrap="wrap"
          spacing={1}
          align="center"
          lineHeight="1.25"
          flexShrink={0}
        >
          <Button
            variant="link"
            size="sm"
            onClick={() => navigate("/analyst")}
            color="blue.600"
            fontWeight="600"
            fontSize="sm"
            minH="auto"
            p={0}
            h="auto"
            textDecoration="underline"
            textUnderlineOffset="2px"
            _hover={{ color: "blue.700", textDecoration: "underline" }}
          >
            Analysts
          </Button>
          <Text as="span" color="gray.400" fontSize="xs" mb={0} userSelect="none" aria-hidden>
            /
          </Text>
          <Button
            variant="link"
            size="sm"
            onClick={() => navigate(`${pathBase}/jobs`)}
            color="blue.600"
            fontWeight="600"
            fontSize="sm"
            minH="auto"
            p={0}
            h="auto"
            textDecoration="underline"
            textUnderlineOffset="2px"
            _hover={{ color: "blue.700", textDecoration: "underline" }}
          >
            {analystName || "Analyst"}
          </Button>
          <Text as="span" color="gray.400" fontSize="xs" mb={0} userSelect="none" aria-hidden>
            /
          </Text>
          <Button
            variant="link"
            size="sm"
            onClick={handleBackToList}
            color="blue.600"
            fontWeight="600"
            fontSize="sm"
            minH="auto"
            p={0}
            h="auto"
            textDecoration="underline"
            textUnderlineOffset="2px"
            _hover={{ color: "blue.700", textDecoration: "underline" }}
          >
            Workflows
          </Button>
          <Text as="span" color="gray.400" fontSize="xs" mb={0} userSelect="none" aria-hidden>
            /
          </Text>
          <Text as="span" color="gray.600" fontWeight="500" fontSize="sm" mb={0}>
            {jobDisplayName}
          </Text>
        </HStack>

        {/* Job editor — owns its own chrome (name, tabs, Run button) */}
        <Box flex="1" minH={0} display="flex" flexDirection="column" overflow="hidden" bg="white">
          <JobViewComponent
            onBack={handleBackToList}
            isCreatingNew={isCreatingNew}
            selectedJob={selectedJob}
          />
        </Box>
      </Flex>
    );
  }

  // While waiting to open a deep-linked job, show a full-page spinner instead of the list
  if (initialJobId && isLoading) {
    return (
      <Center height="100%">
        <Spinner size="xl" color="purple.600" thickness="3px" />
      </Center>
    );
  }

  return (
    <Box height="100%" bg="white" p={4}>
      {/* Header: search bar + create button */}
      <HStack justify="space-between" align="center" mb={1} spacing={4}>
        <Text fontSize="sm" fontWeight="500" color="gray.400" m={0} minW="150px" />
        {/* Search Bar */}
        <InputGroup size="md" maxW="500px" flex="1">
          <InputLeftElement pointerEvents="none" height="full">
            <SearchIcon color="gray.400" boxSize={4} />
          </InputLeftElement>
          <Input
            placeholder="Search analyses by name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            bg="gray.50"
            border="1px"
            borderColor="gray.200"
            fontSize="sm"
            _hover={{ borderColor: "gray.300", bg: "white" }}
            _focus={{
              borderColor: "purple.400",
              boxShadow: "0 0 0 1px var(--chakra-colors-purple-400)",
              bg: "white",
            }}
            _placeholder={{ color: "gray.400" }}
          />
        </InputGroup>

        {/* Create New Workflow Button */}
        <Button
          variant="outline"
          size="md"
          fontWeight="700"
          colorScheme="purple"
          leftIcon={<Icon as={FaPlus} boxSize={4} />}
          onClick={handleCreateNewJob}
        >
          New Workflow
        </Button>
      </HStack>

      {/* Job List */}
      <JobList
        jobs={filteredJobs}
        isLoadingJobs={isLoading}
        onJobClick={handleJobClick}
        onRefresh={handleRefresh}
      />
    </Box>
  );
};

export default AnalystJobsComponent;
